import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listSavedPaymentMethods,
  deleteSavedPaymentMethod,
  setDefaultPaymentMethod,
} from '../api/checkout';
import { useUnifiedAuth } from '../contexts/UnifiedAuthContext';
import useLatestRequest from './useLatestRequest';

const emptyState = {
  paymentMethods: [],
};

const useSavedPaymentMethods = ({ enabled = true } = {}) => {
  const { user, isAuthenticated, getApiToken } = useUnifiedAuth();
  const userId = user?.id;
  const { begin, cancel } = useLatestRequest();
  const session = useRef(0);
  const mutations = useRef(0);
  const [state, setState] = useState(emptyState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  const fetchToken = useCallback(async () => {
    const token = enabled && isAuthenticated ? await getApiToken() : null;
    if (!token) throw new Error('Authentication required to manage payment methods');
    return token;
  }, [enabled, getApiToken, isAuthenticated]);

  const refresh = useCallback(async () => {
    const signal = begin();
    setError(null);
    if (!enabled || !isAuthenticated) {
      setState(emptyState);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = await fetchToken();
      if (signal.aborted) return;
      const payload = await listSavedPaymentMethods(token, signal);
      if (!signal.aborted) setState(payload || emptyState);
    } catch (refreshError) {
      if (signal.aborted) return;
      console.error('[useSavedPaymentMethods] Failed to load payment methods', refreshError);
      setError(refreshError);
      setState(emptyState);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [enabled, fetchToken, isAuthenticated, begin]);

  useEffect(() => {
    setState(emptyState);
    setPending(mutations.current > 0);
    void refresh();
    return () => {
      session.current += 1;
      mutations.current = 0;
      cancel();
    };
  }, [refresh, cancel, userId]);

  const runMutation = useCallback(
    async (mutator) => {
      const owner = session.current;
      mutations.current += 1;
      setPending(true);
      setError(null);
      try {
        const token = await fetchToken();
        if (owner !== session.current) throw new Error('Payment session changed');
        await mutator(token);
        if (owner === session.current) await refresh();
      } catch (mutationError) {
        if (owner === session.current) setError(mutationError);
        throw mutationError;
      } finally {
        if (owner === session.current) {
          mutations.current -= 1;
          setPending(mutations.current > 0);
        }
      }
    },
    [fetchToken, refresh],
  );

  const remove = useCallback(
    (paymentMethodId) =>
      runMutation((token) => deleteSavedPaymentMethod(paymentMethodId, token || undefined)),
    [runMutation],
  );

  const setDefault = useCallback(
    (paymentMethodId) =>
      runMutation((token) => setDefaultPaymentMethod(paymentMethodId, token || undefined)),
    [runMutation],
  );

  return useMemo(
    () => ({
      paymentMethods: state.paymentMethods,
      loading,
      error,
      pending,
      refresh,
      remove,
      setDefault,
      hasMethods: state.paymentMethods.length > 0,
    }),
    [error, loading, pending, refresh, remove, setDefault, state.paymentMethods],
  );
};

export default useSavedPaymentMethods;
